const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' 
    ? 'http://localhost:5001/api/v1' 
    : 'https://over-soul-be.onrender.com/api/v1');

class SanctumApiService {
  constructor() {
    this.token = localStorage.getItem('sanctum_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('sanctum_token', token);
    } else {
      localStorage.removeItem('sanctum_token');
    }
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    // Add authorization header if token exists
    if (this.token) {
      config.headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          this.setToken(null);
          window.location.href = '/sanctum/login';
        }
        throw new Error(data.error || data.message || 'Something went wrong');
      }

      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Auth endpoints
  async login(email, password) {
    const response = await this.request('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async getCurrentUser() {
    return this.request('/admin/auth/me');
  }

  async checkSetupStatus() {
    // This endpoint doesn't require authentication
    const savedToken = this.token;
    this.token = null;
    try {
      const response = await this.request('/admin/check-setup');
      this.token = savedToken;
      return response;
    } catch (error) {
      this.token = savedToken;
      throw error;
    }
  }

  async setupFirstAdmin(name, email, password) {
    const response = await this.request('/admin/setup', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  // Admin management endpoints
  async getAdmins() {
    return this.request('/admin/admins');
  }

  async createAdmin(adminData) {
    return this.request('/admin/admins', {
      method: 'POST',
      body: JSON.stringify(adminData),
    });
  }

  async updateAdmin(id, adminData) {
    return this.request(`/admin/admins/${id}`, {
      method: 'PUT',
      body: JSON.stringify(adminData),
    });
  }

  async deleteAdmin(id) {
    return this.request(`/admin/admins/${id}`, {
      method: 'DELETE',
    });
  }

  // Waitlist endpoints
  async getWaitlist(page = 1, limit = 50, search = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return this.request(`/admin/waitlist?${params}`);
  }

  // User management endpoints
  async getUsers(page = 1, limit = 50, search = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return this.request(`/admin/users?${params}`);
  }

  async getUserDetails(userId) {
    return this.request(`/admin/users/${userId}`);
  }

  logout() {
    this.setToken(null);
  }
}

const sanctumApi = new SanctumApiService();
export default sanctumApi;